import { isCancel, log, password, select, spinner } from "@clack/prompts";
import type { AuthMethod, Profile } from "./types";

/**
 * Interactive prompts (TTY only) built on `@clack/prompts`.
 *
 * On Ctrl-C / cancel, clack returns a cancel symbol; we throw
 * `PromptCancelledError` so the command runner can convert it into a clean
 * error result with exit code 130.
 */

export class PromptCancelledError extends Error {
	constructor() {
		super("Prompt cancelled");
		this.name = "PromptCancelledError";
	}
}

function ensure<T>(value: T | symbol): T {
	if (isCancel(value)) throw new PromptCancelledError();
	return value;
}

export async function chooseAuthMethod(): Promise<AuthMethod> {
	const method = await select<AuthMethod>({
		message: "How do you want to authenticate?",
		options: [
			{
				value: "oauth",
				label: "Log in with Whop",
				hint: "Opens your browser to authorize",
			},
			{
				value: "api_key",
				label: "API key",
				hint: "Paste a Whop account API key",
			},
		],
		initialValue: "oauth",
	});
	return ensure(method);
}

export async function promptApiKey(): Promise<string> {
	const key = await password({
		message: "Paste your Whop API key:",
		validate: (value) =>
			value && value.trim().length > 0 ? undefined : "An API key is required",
	});
	return ensure(key).trim();
}

export interface CompanyChoice {
	id: string;
	title: string;
	route: string;
}

export async function selectCompany(
	companies: CompanyChoice[],
): Promise<CompanyChoice> {
	const id = await select<string>({
		message: "Which business account do you want to use?",
		options: companies.map((company) => ({
			value: company.id,
			label: company.title,
			hint: company.id,
		})),
	});
	const chosen = ensure(id);
	const match = companies.find((company) => company.id === chosen);
	if (!match) {
		throw new Error(
			`Selected business account "${chosen}" is no longer available.`,
		);
	}
	return match;
}

export async function selectProfile(profiles: Profile[]): Promise<string> {
	const name = await select<string>({
		message: "Select a profile to switch to:",
		options: profiles.map((profile) => ({
			value: profile.name,
			label: profile.name,
			hint:
				profile.accountTitle ??
				profile.userEmail ??
				profile.accountId ??
				profile.userId,
		})),
	});
	return ensure(name);
}

/** Starts a clack spinner and returns it so the caller can `.stop(message)`. */
export function startSpinner(message: string): ReturnType<typeof spinner> {
	const s = spinner();
	s.start(message);
	return s;
}

/** Prints an informational line (e.g. a manual fallback URL or hint). */
export function printInfo(message: string): void {
	log.info(message);
}
